import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useParts } from '@/hooks/useParts';
import { deletePart } from '@/lib/parts';
import { categoryLabel } from '@/data/categories';
import AdminLayout from '@/components/admin/AdminLayout';

export default function InventoryManager() {
  const { parts, loading, error } = useParts();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const filtered = parts.filter((p) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      p.sku.toLowerCase().includes(q) ||
      (p.casting?.toLowerCase().includes(q) ?? false) ||
      String(p.year).includes(q)
    );
  });

  async function handleDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"? Cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await deletePart(id);
      toast.success('Part deleted', { description: title });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error('Delete failed', { description: msg });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <AdminLayout>
      <div className="row between" style={{ marginBottom: 16 }}>
        <div>
          <div className="eyebrow">Inventory Manager</div>
          <h1 className="h-title" style={{ margin: 0 }}>All Parts</h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/admin/inventory/import" className="btn dark sm">↑ Import CSV</Link>
          <Link to="/admin/inventory/new" className="btn sm">＋ Add Part</Link>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 18 }}>
        <div className="filter-search">
          <input
            type="text"
            placeholder="Search by title, SKU, casting, or year…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.15em' }}>
          {loading ? 'LOADING…' : `${filtered.length} / ${parts.length} SHOWN`}
        </div>
      </div>

      {error && <div className="auth-error" style={{ marginBottom: 16 }}>Firestore error: {error}</div>}

      {!loading && parts.length === 0 ? (
        <div className="empty-state" style={{ padding: 60, background: 'var(--bg-1)', border: '1px dashed var(--line-2)' }}>
          <div style={{ fontFamily: 'var(--display)', fontSize: 32, color: 'var(--text-dim)' }}>No parts yet.</div>
          <p style={{ margin: '12px 0 20px' }}>Seed the sample catalog from the Dashboard, or add one manually.</p>
          <Link to="/admin/inventory/new" className="btn">＋ Add First Part</Link>
        </div>
      ) : (
        <div className="table-scroll">
        <table className="admin-table cards-on-mobile">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Part</th>
              <th>Year</th>
              <th>Cat</th>
              <th>Type</th>
              <th>Cond</th>
              <th style={{ textAlign: 'right' }}>Price</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id}>
                <td data-label="SKU" className="sku">{p.sku}</td>
                <td data-label="Part"><strong style={{ color: 'var(--cream)' }}>{p.title}</strong></td>
                <td data-label="Year">{p.year}</td>
                <td data-label="Category"><small style={{ color: 'var(--text-dim)' }}>{categoryLabel(p.category)}</small></td>
                <td data-label="Type"><span className={`badge ${p.type === 'OEM' ? 'oem' : 'repro'}`}>{p.type}</span></td>
                <td data-label="Cond"><small>{p.condition}</small></td>
                <td data-label="Price" className="price" style={{ textAlign: 'right' }}>${p.price.toLocaleString()}</td>
                <td data-label="Status">
                  <span className={`badge ${p.status === 'live' ? 'live' : p.status === 'reserved' ? 'reserved' : p.status === 'sold' ? 'sold' : ''}`}>
                    {p.status}
                  </span>
                </td>
                <td className="actions">
                  <button type="button" className="btn sm dark" onClick={() => navigate(`/admin/inventory/${p.id}/edit`)}>Edit</button>
                  <button
                    type="button"
                    className="btn sm danger"
                    onClick={() => handleDelete(p.id, p.title)}
                    disabled={deletingId === p.id}
                  >
                    {deletingId === p.id ? '…' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </AdminLayout>
  );
}
